import { DatePipe, DecimalPipe, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  Signal,
  signal,
  TemplateRef,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { PartnerBundleDialogComponent } from '@app/modules/partners/components/partner-bundle-dialog/partner-bundle-dialog.component';
import { DiscountUtilsService } from '@app/modules/pricing/services/discount-utils.service';
import { PricingPartnerUtilsService } from '@app/modules/pricing/services/pricing-partner-utils.service';
import { Bundle, PaymentType } from '@core/core.types';
import { LoadBundleConfigurationByIds } from '@core/store/bundle-configuration/bundle-configuration.actions';
import { LoadPartnerById } from '@core/store/partners/partners.actions';
import { PartnersState } from '@core/store/partners/partners.state';
import { ProductsListState } from '@core/store/products/products.state';
import { CoreUtilsService } from '@core/utils/core-utils.service';
import {
  NgbActiveModal,
  NgbModal,
  NgbTooltipModule,
} from '@ng-bootstrap/ng-bootstrap';
import {
  TranslateModule,
  TranslatePipe,
  TranslateService,
} from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ConfirmService } from '@shared/components/confirm/confirm-dialog.component';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import {
  DataTableActionsLocation,
  DataTableActionType,
  DataTableConfiguration,
  DataTableDataType,
  DataTableSelectionType,
} from '@shared/components/data-table/data-table.types';
import { LoadingService } from '@shared/components/loading-dialog/loading-dialog.component';
import { UserDisplayComponent } from '@shared/components/user-display/user-display.component';
import { RequirePermissionDirective } from '@shared/directives/require-permission.directive';
import { ConfirmCloseReason } from '@shared/shared.types';
import moment from 'moment';
import { filter, first, map, switchMap, timer } from 'rxjs';
import { DiscountState } from '../../../store/discount.state';
import { ApprovePriceRequestsAction } from '../../../store/price-request.actions';
import {
  ApprovePricingPartnerPriceRequestAction,
  DeletePricingPartnerBundlePriceAction,
  DeletePricingPartnerPriceRequestAction,
  GetPricingPartnerAction,
} from '../../../store/pricing-partner.actions';
import { PricingPartnerState } from '../../../store/pricing-partner.state';
import {
  BundlePrice,
  Discount,
  DiscountPriceType,
  DiscountStatus,
  DiscountType,
  PartnerBundle,
  PricingPartner,
  ValueType,
} from '../../../types/pricing.types';
import { BulkPriceDialogComponent } from '../bulk-price-dialog/bulk-price-dialog.component';
import { BundlePriceFormComponent } from '../bundle-price-form/bundle-price-form.component';
import { DoctorsFeeDialogComponent } from '../doctors-fee-dialog/doctors-fee-dialog.component';
import { EditPriceDialogComponent } from '../edit-price-dialog/edit-price-dialog.component';
import { PriceHistoryDialogComponent } from '../price-history-dialog/price-history-dialog.component';

@Component({
  selector: 'app-pricing-partner-dialog',
  imports: [
    TranslateModule,
    DataTableComponent,
    BundlePriceFormComponent,
    UserDisplayComponent,
    TranslatePipe,
    NgbTooltipModule,
    RequirePermissionDirective,
    DecimalPipe,
    DatePipe,
    NgTemplateOutlet,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
  ],
  templateUrl: './pricing-partner-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './pricing-partner-dialog.component.scss',
})
export class PricingPartnerDialogComponent implements OnInit {
  public partnerId = signal<string>(null);
  protected modalRef = inject(NgbActiveModal);
  protected coreUtilsService = inject(CoreUtilsService);
  private store = inject(Store);
  private ngbModal = inject(NgbModal);
  private confirmService = inject(ConfirmService);
  private loadingService = inject(LoadingService);
  private translateService = inject(TranslateService);
  private discountUtilsService = inject(DiscountUtilsService);
  private pricingPartnerUtilsService = inject(PricingPartnerUtilsService);

  protected pricingPartner: Signal<PricingPartner>;
  protected bundlesList: Signal<Bundle[]>;

  /**
   * A partner whose only payment type is "Partner" sees just its WSP prices:
   * no country EUP, no doctors fee column and no doctors fee dialog. Any
   * other combination of payment types shows everything.
   */
  protected wspOnly: Signal<boolean> = computed(() => {
    const paymentTypes = this.pricingPartner()?.paymentTypes ?? [];

    return (
      paymentTypes.length === 1 &&
      paymentTypes[0]?.toLowerCase() === PaymentType.Partner.toLowerCase()
    );
  });

  protected bundleDataTableConfiguration: Signal<DataTableConfiguration> =
    computed(() => this.buildBundleDataTableConfiguration(this.wspOnly()));
  protected discountDataTableConfiguration: DataTableConfiguration;

  private datePipe = inject(DatePipe);

  private priceColumnTemplate = viewChild<TemplateRef<any>>(
    'priceColumnTemplate'
  );
  private bundleNameColumnTemplate = viewChild<TemplateRef<any>>(
    'bundleNameColumnTemplate'
  );
  private discountValueColumnTemplate = viewChild<TemplateRef<any>>(
    'discountValueColumnTemplate'
  );
  private bundlesColumnTemplate = viewChild<TemplateRef<any>>(
    'bundlesColumnTemplate'
  );
  private endUserPriceColumnTemplate = viewChild<TemplateRef<any>>(
    'endUserPriceColumnTemplate'
  );
  private doctorsFeeColumnTemplate = viewChild<TemplateRef<any>>(
    'doctorsFeeColumnTemplate'
  );
  private generatedByColumnTemplate = viewChild<TemplateRef<any>>(
    'generatedByColumnTemplate'
  );
  private lastStatusColumnTemplate = viewChild<TemplateRef<any>>(
    'lastStatusColumnTemplate'
  );

  constructor() {
    effect(() => {
      this.store.dispatch(new GetPricingPartnerAction(this.partnerId()));
    });
  }

  ngOnInit(): void {
    this.pricingPartner = computed(() =>
      this.store.selectSignal(PricingPartnerState.getPricingPartnerById)()(
        this.partnerId()
      )
    );

    this.bundlesList = computed(() =>
      this.store.selectSignal(ProductsListState.getBundlesList)()
    );

    this.discountDataTableConfiguration =
      this.buildDiscountDataTableConfiguration();
  }

  private buildBundleDataTableConfiguration(
    wspOnly: boolean
  ): DataTableConfiguration {
    return {
      title: '',
      selectionMode: DataTableSelectionType.NONE,
      tableHeadClasses: '',
      actionsLocation: DataTableActionsLocation.RIGHT,
      singleActions: [
        {
          name: 'pricing.partner.details.actions.editPrice',
          icon: 'fa-light fa-pen',
          type: DataTableActionType.BUTTON,
          permission: 'pricing.partner.actions',
          invisible: (bundle: BundlePrice) => {
            return !!bundle.pivot.price || !!bundle.isPriceRequest;
          },
          disabled: () => {
            return !this.pricingPartner()?.currency;
          },
          action: this.editPrice.bind(this),
        },
        {
          name: 'pricing.partner.details.actions.priceHistory',
          icon: 'fa-light fa-list',
          type: DataTableActionType.BUTTON,
          invisible: (bundle: BundlePrice) => !!bundle.isPriceRequest,
          disabled: (bundle: BundlePrice) => {
            return !bundle.pivot.price;
          },
          action: this.openPriceHistory.bind(this),
        },
        {
          name: 'pricing.partner.details.actions.approvePriceRequest',
          icon: 'fa-light fa-check',
          type: DataTableActionType.BUTTON,
          permission: 'pricing.partner.actions.approvePriceRequest',
          invisible: (bundle: BundlePrice) => !bundle.isPriceRequest,
          action: this.approvePriceRequest.bind(this),
        },
        {
          name: 'pricing.partner.details.actions.deleteLatestPrice',
          icon: 'fa-light fa-xmark',
          type: DataTableActionType.BUTTON,
          permission: 'pricing.partner.actions',
          disabled: (bundle: BundlePrice) => {
            return (
              !!bundle.isPriceRequest ||
              !bundle.pivot.price ||
              moment(bundle.pivot.validFrom).isBefore(moment())
            );
          },
          invisible: (bundle: BundlePrice) => !!bundle.isPriceRequest,
          action: this.deletePrice.bind(this),
        },
        {
          name: 'pricing.partner.details.actions.deletePriceRequest',
          icon: 'fa-light fa-trash',
          type: DataTableActionType.BUTTON,
          invisible: (bundle: BundlePrice) => !bundle.isPriceRequest,
          action: this.deletePriceRequest.bind(this),
        },
      ],
      columns: [
        {
          label: 'pricing.partner.details.columns.bundle',
          propertyTemplate: this.bundleNameColumnTemplate(),
        },
        {
          label: 'pricing.partner.details.columns.wholesalePrice',
          propertyTemplate: this.priceColumnTemplate(),
        },
        // Country EUP and doctors fee are hidden for "Partner"-only partners.
        ...(wspOnly
          ? []
          : [
              {
                label: 'pricing.partner.details.columns.endUserPrice',
                propertyTemplate: this.endUserPriceColumnTemplate(),
              },
              {
                label: 'pricing.partner.details.columns.doctorsFee',
                propertyTemplate: this.doctorsFeeColumnTemplate(),
              },
            ]),
        {
          label: 'pricing.partner.details.columns.generatedBy',
          propertyTemplate: this.generatedByColumnTemplate(),
        },
      ],
      rows$: this.store.select(PricingPartnerState.getPricingPartnerById).pipe(
        map((pricingPartner) => pricingPartner(this.partnerId())),
        map((pricingPartner) => this.getPartnerBundles(pricingPartner)),
        filter((bundles: BundlePrice[]) => !!bundles && !!bundles?.length),
        map((bundles: BundlePrice[]) =>
          [...bundles].sort((a, b) => this.sortPartnerBundles(a, b))
        )
      ),
    };
  }

  private buildDiscountDataTableConfiguration(): DataTableConfiguration {
    return {
      title: '',
      selectionMode: DataTableSelectionType.NONE,
      tableHeadClasses: '',
      actionsLocation: DataTableActionsLocation.RIGHT,
      singleActions: [],
      columns: [
        {
          label: 'pricing.partner.details.discountColumns.discountType',
          property: 'discountType',
        },
        {
          label: 'pricing.partner.details.discountColumns.priceType',
          invoke: {
            objectReference: {
              getPriceType: (discount: Discount) =>
                this.translateService.instant(
                  'pricing.discount.list.priceType.' +
                    (discount.priceType ?? DiscountPriceType.WSP)
                ),
            },
            name: 'getPriceType',
            args: [],
          },
        },
        {
          label: 'pricing.partner.details.discountColumns.validFrom',
          invoke: {
            objectReference: {
              getDate: (discount: Discount) =>
                `${
                  discount.discountType === DiscountType.General
                    ? this.datePipe.transform(discount.validFrom, 'dd.MM.yyyy')
                    : '—'
                }`,
            },
            name: 'getDate',
            args: [],
          },
          type: DataTableDataType.DATE,
        },
        {
          label: 'pricing.partner.details.discountColumns.validTo',
          invoke: {
            objectReference: {
              getDate: (discount: Discount) =>
                `${
                  discount.discountType === DiscountType.General
                    ? this.datePipe.transform(discount.validTo, 'dd.MM.yyyy')
                    : '—'
                }`,
            },
            name: 'getDate',
            args: [],
          },
          type: DataTableDataType.DATE,
        },
        {
          label: 'pricing.partner.details.discountColumns.discount',
          propertyTemplate: this.discountValueColumnTemplate(),
        },
        {
          label: 'pricing.partner.details.discountColumns.bundles',
          propertyTemplate: this.bundlesColumnTemplate(),
        },
        {
          label: 'pricing.partner.details.discountColumns.status',
          propertyTemplate: this.lastStatusColumnTemplate(),
        },
        {
          label: 'pricing.partner.details.discountColumns.notes',
          property: 'reason',
          type: DataTableDataType.TEXT,
        },
      ],
      rows$: this.store
        .select(DiscountState.getDiscountsByPartnerId)
        .pipe(
          map((getDiscountsByPartnerId) =>
            getDiscountsByPartnerId(this.partnerId())
          )
        ),
    };
  }

  protected getBundleById(bundleId: string) {
    return this.bundlesList().find((bundle) => bundle.id === bundleId);
  }

  protected getPartnerBundles(pricingPartner: PricingPartner): BundlePrice[] {
    return [
      ...(pricingPartner?.pricelist ?? []),
      ...(pricingPartner?.priceRequests ?? []),
    ].filter((bundle) => !!bundle);
  }

  /**
   * Doctors fee = country EUP - partner WSP; null unless both are known.
   * Uses the undiscounted prices: an EUP discount lowers both by the same
   * amount, so the fee stays unchanged.
   */
  protected getDoctorsFee(bundle: BundlePrice): number | null {
    const wsp = bundle?.pivot?.price;
    const eup = bundle?.countryEupPrice;

    if (bundle?.isPriceRequest || !wsp || !eup) {
      return null;
    }

    return Math.round((eup - wsp) * 100) / 100;
  }

  protected getBundleDisplayName(bundle: BundlePrice): string {
    return bundle?.internalName ?? bundle?.name ?? '';
  }

  private sortPartnerBundles(a: BundlePrice, b: BundlePrice): number {
    const nameComparison = this.getBundleDisplayName(a).localeCompare(
      this.getBundleDisplayName(b),
      undefined,
      { sensitivity: 'base' }
    );

    if (nameComparison !== 0) {
      return nameComparison;
    }

    if (!!a.isPriceRequest === !!b.isPriceRequest) {
      return 0;
    }

    return a.isPriceRequest ? 1 : -1;
  }

  protected hasPendingPriceRequests(): boolean {
    return this.getPendingPriceRequests().length > 0;
  }

  private editPrice(e: Event, bundle: PartnerBundle) {
    const modalRef = this.ngbModal.open(EditPriceDialogComponent, {
      size: 'md',
      centered: true,
    });

    const pricingPartnerBundle = this.getPartnerBundles(
      this.pricingPartner()
    ).find((b) => bundle.id === b.id);

    modalRef.componentInstance.partnerId.set(this.partnerId());
    modalRef.componentInstance.bundleId.set(bundle.id);
    modalRef.componentInstance.price.set(pricingPartnerBundle.pivot.price);
  }

  private deletePrice(e: Event, bundle: BundlePrice) {
    this.confirmService
      .confirm({
        title: this.translateService.instant(
          'pricing.partner.details.deletePrice.title'
        ),
        message: this.translateService.instant(
          'pricing.partner.details.deletePrice.message'
        ),
      })
      .subscribe((result) => {
        if (result.closeReason === ConfirmCloseReason.Yes) {
          this.store.dispatch(
            new DeletePricingPartnerBundlePriceAction(
              this.partnerId(),
              bundle.pivot.id
            )
          );
        }
      });
  }

  private approvePriceRequest(e: Event, bundle: BundlePrice) {
    const hasPastValidFrom = moment(bundle.pivot.validFrom).isBefore(moment());

    this.confirmService
      .confirm({
        title: hasPastValidFrom
          ? this.translateService.instant(
              'pricing.partner.details.approvePriceRequest.pastTitle'
            )
          : this.translateService.instant(
              'pricing.partner.details.approvePriceRequest.title'
            ),
        message: hasPastValidFrom
          ? this.translateService.instant(
              'pricing.partner.details.approvePriceRequest.pastMessage'
            )
          : this.translateService.instant(
              'pricing.partner.details.approvePriceRequest.message'
            ),
      })
      .subscribe((result) => {
        if (result.closeReason === ConfirmCloseReason.Yes) {
          this.loadingService.wait({
            message: this.translateService.instant(
              'pricing.partner.details.loading.approvingPriceRequest'
            ),
            observableToFinish$: this.store.dispatch(
              new ApprovePricingPartnerPriceRequestAction(
                this.partnerId(),
                bundle.id
              )
            ),
          });
        }
      });
  }

  protected approveAllPendingRequests(event: MouseEvent) {
    event.preventDefault();

    const pendingPriceRequests = this.getPendingPriceRequests();
    const pendingIds = pendingPriceRequests.map((bundle) => bundle.id);
    const hasPastValidFrom = pendingPriceRequests.some((bundle) =>
      moment(bundle.pivot.validFrom).isBefore(moment())
    );

    if (!pendingIds.length) {
      return;
    }

    this.confirmService
      .confirm({
        title: hasPastValidFrom
          ? this.translateService.instant(
              'pricing.partner.details.approvePriceRequests.pastTitle'
            )
          : this.translateService.instant(
              'pricing.partner.details.approvePriceRequests.title'
            ),
        message: hasPastValidFrom
          ? this.translateService.instant(
              'pricing.partner.details.approvePriceRequests.pastMessage'
            )
          : this.translateService.instant(
              'pricing.partner.details.approvePriceRequests.message'
            ),
      })
      .subscribe((result) => {
        if (result.closeReason === ConfirmCloseReason.Yes) {
          this.loadingService.wait({
            message: this.translateService.instant(
              'pricing.partner.details.loading.approvingPriceRequests'
            ),
            observableToFinish$: this.store
              .dispatch(new ApprovePriceRequestsAction(pendingIds))
              .pipe(
                switchMap(() =>
                  this.store.dispatch(
                    new GetPricingPartnerAction(this.partnerId())
                  )
                )
              ),
          });
        }
      });
  }

  private deletePriceRequest(e: Event, bundle: BundlePrice) {
    this.confirmService
      .confirm({
        title: this.translateService.instant(
          'pricing.partner.details.deletePriceRequest.title'
        ),
        message: this.translateService.instant(
          'pricing.partner.details.deletePriceRequest.message'
        ),
      })
      .subscribe((result) => {
        if (result.closeReason === ConfirmCloseReason.Yes) {
          this.loadingService.wait({
            message: this.translateService.instant(
              'pricing.partner.details.loading.deletingPriceRequest'
            ),
            observableToFinish$: this.store.dispatch(
              new DeletePricingPartnerPriceRequestAction(
                this.partnerId(),
                bundle.id
              )
            ),
          });
        }
      });
  }

  protected openBundleListDialog(event: MouseEvent, discount: Discount) {
    event.preventDefault();
    event.stopPropagation();

    this.discountUtilsService.openBundleListDialog(discount?.bundleIds);
  }

  protected openCurrencyDialog(event: MouseEvent) {
    event.preventDefault();

    this.pricingPartnerUtilsService.openEditCurrencyDialog(this.partnerId());
  }

  protected openBulkPriceDialog(event: MouseEvent) {
    event.preventDefault();

    const modalRef = this.ngbModal.open(BulkPriceDialogComponent, {
      centered: true,
      modalDialogClass: 'modal-size-lg',
    });

    modalRef.componentInstance.partnerId.set(this.partnerId());
  }

  protected openDoctorsFeeDialog(event: MouseEvent) {
    event.preventDefault();

    const modalRef = this.ngbModal.open(DoctorsFeeDialogComponent, {
      centered: true,
      modalDialogClass: 'modal-size-xxl',
    });

    modalRef.componentInstance.partnerId.set(this.partnerId());
  }

  protected openEditBundlesDialog(event: MouseEvent) {
    event.preventDefault();

    const partnerId = this.partnerId();
    this.store
      .dispatch(new LoadPartnerById(partnerId))
      .pipe(
        switchMap(() => this.store.select(PartnersState.getPartnerById)),
        map((getPartnerById) => getPartnerById(partnerId)),
        filter((partner) => !!partner),
        switchMap((partner) =>
          this.store
            .dispatch(
              new LoadBundleConfigurationByIds(partner.bundleConfigurations)
            )
            .pipe(map(() => partner))
        ),
        first()
      )
      .subscribe((partner) => {
        const modalRef = this.ngbModal.open(PartnerBundleDialogComponent, {
          centered: true,
          modalDialogClass: 'modal-size-md',
        });

        modalRef.componentInstance.partner.set(partner);

        modalRef.closed.subscribe((changedBundlesCount: number) => {
          if (!changedBundlesCount) {
            return;
          }

          const syncDelayMs = 1000 + 350 * changedBundlesCount;

          this.loadingService.wait({
            message: 'pricing.partner.details.syncingBundles',
            cancelButton: false,
            observableToFinish$: timer(syncDelayMs).pipe(
              switchMap(() =>
                this.store.dispatch(
                  new GetPricingPartnerAction(this.partnerId())
                )
              )
            ),
          });
        });
      });
  }

  private openPriceHistory(e: Event, bundle: BundlePrice) {
    const modalRef = this.ngbModal.open(PriceHistoryDialogComponent, {
      size: 'lg',
      centered: true,
    });

    modalRef.componentInstance.partnerId.set(this.partnerId());
    modalRef.componentInstance.bundle.set(bundle);
  }

  private getPendingPriceRequests(): BundlePrice[] {
    return (this.pricingPartner()?.priceRequests ?? []).filter(
      (bundle) => !!bundle?.isPriceRequest
    );
  }

  protected readonly ValueType = ValueType;
  protected readonly DiscountType = DiscountType;
  protected readonly DiscountStatus = DiscountStatus;
}
